import { ReactNode } from 'react'

import { isSellOrder } from '@cowprotocol/common-utils'
import { SupportedChainId } from '@cowprotocol/cow-sdk'
import { TokenInfo } from '@cowprotocol/types'

import { Trans } from '@lingui/react/macro'

import { useOrder } from 'legacy/state/orders/hooks'

import { useNotifyAffiliateLinkedCode } from 'modules/affiliate'

import { useGetExecutedBridgeSummary } from 'common/hooks/useGetExecutedBridgeSummary'
import { useGetSurplusData } from 'common/hooks/useGetSurplusFiatValue'
import { OrderSummary } from 'common/pure/OrderSummary'
import { OrderSummaryTemplateProps } from 'common/pure/OrderSummary/summaryTemplates'

import * as styledEl from './styled'

interface ExecutedSummaryProps {
  chainId: SupportedChainId
  orderUid: string
}

export function FulfilledOrderInfo({ chainId, orderUid }: ExecutedSummaryProps): ReactNode {
  const order = useOrder({ chainId, id: orderUid })
  const surplusData = useGetSurplusData(order)
  useNotifyAffiliateLinkedCode({ order, chainId })

  const { surplusFiatValue, showFiatValue, surplusToken, surplusAmount } = surplusData

  const { formattedFilledAmount, formattedSwappedAmount } = useGetExecutedBridgeSummary(order) || {}

  if (!order || !formattedSwappedAmount || !formattedFilledAmount) return null

  const inputToken = isSellOrder(order.kind) ? order.inputToken : formattedSwappedAmount.currency
  const outputToken = isSellOrder(order.kind) ? formattedSwappedAmount.currency : order.outputToken

  const sellAmount = isSellOrder(order.kind)
    ? formattedFilledAmount.quotient.toString()
    : formattedSwappedAmount.quotient.toString()
  const buyAmount = isSellOrder(order.kind)
    ? formattedSwappedAmount.quotient.toString()
    : formattedFilledAmount.quotient.toString()

  return (
    <>
      {formattedFilledAmount?.currency && (
        <OrderSummary
          kind={order.kind}
          inputToken={inputToken as TokenInfo}
          outputToken={outputToken as TokenInfo}
          sellAmount={sellAmount}
          buyAmount={buyAmount}
          customTemplate={FulfilledSummaryTemplate}
        />
      )}
      {!!surplusAmount && (
        <styledEl.SurplusWrapper>
          <span>
            <Trans>Order surplus</Trans>:{' '}
          </span>
          <styledEl.SurplusAmount>
            <styledEl.StyledTokenAmount amount={surplusAmount} tokenSymbol={surplusToken} />
            {showFiatValue && (
              <styledEl.FiatWrapper>
                (<styledEl.StyledFiatAmount amount={surplusFiatValue} />)
              </styledEl.FiatWrapper>
            )}
          </styledEl.SurplusAmount>
        </styledEl.SurplusWrapper>
      )}
    </>
  )
}

function FulfilledSummaryTemplate({ inputAmount, outputAmount }: OrderSummaryTemplateProps): ReactNode {
  return (
    <>
      <Trans>
        Traded {inputAmount} for a total of {outputAmount}
      </Trans>
    </>
  )
}
